import React, { useState } from 'react';
import { FormComponent, UiComponent } from 'components';
import { usePageOption } from 'hooks';
import { ContentsQuery } from 'queries';
import { ContentsPageType, IContent, SearchContentsFilterType } from 'types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDrag, useDrop } from 'react-dnd';
import { wrap, searchWrap, selectedWrap } from './style';

interface Props {
  pageType: ContentsPageType;
  contents: IContent[];
  onSubmit: (contents: IContent[]) => void;
}

function SelectContentsModal({ contents, onSubmit, pageType }: Props) {
  const pageState = usePageOption(10);
  const [filter, setFilter] = useState<SearchContentsFilterType>({});
  const { data } = ContentsQuery.useGetAll({
    pageType,
    page: pageState.pageOption.page,
    size: pageState.pageOption.size,
    ...filter,
  });

  const [selectedContents, setSelectedContents] =
    useState<IContent[]>(contents);

  const handleSelect = (c: IContent) => {
    if (selectedContents.find((item) => item.id === c.id)) {
      return;
    }
    setSelectedContents([...selectedContents, c]);
  };

  const handleRemove = (c: IContent) => {
    setSelectedContents(selectedContents.filter((item) => item.id !== c.id));
  };

  const isSelected = (c: IContent) => {
    return selectedContents.find((item) => item.id === c.id);
  };

  // 드래그 가능한 콘텐츠 아이템 컴포넌트
  const DraggableContentItem = ({
    content,
    index,
  }: {
    content: IContent;
    index: number;
  }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
      type: 'CONTENT',
      item: { content, index },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }));

    const [, drop] = useDrop(() => ({
      accept: 'CONTENT',
      drop: (item: { content: IContent; index: number }) => {
        if (item.index === index) return;

        const newContents = [...selectedContents];
        const draggedItem = newContents[item.index];
        newContents.splice(item.index, 1);
        newContents.splice(index, 0, draggedItem);
        setSelectedContents(newContents);
      },
    }));

    return (
      <div
        ref={(node) => {
          drag(node);
          drop(node);
        }}
        css={{
          padding: '8px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          marginBottom: '4px',
          cursor: 'move',
          backgroundColor: isDragging ? '#f0f0f0' : 'white',
          opacity: isDragging ? 0.5 : 1,
        }}
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <p css={{ margin: 0 }}>
            <b css={{ marginRight: '4px' }}>[{content.id}]</b>
            {content.title}
          </p>
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={() => handleRemove(content)}
          >
            제거
          </UiComponent.Button>
        </div>
      </div>
    );
  };

  return (
    <div css={wrap}>
      <div css={searchWrap}>
        <FormComponent.Form submit={setFilter}>
          <FormComponent.SearchInput
            name="title"
            placeholder="콘텐츠명으로 검색해주세요"
            css={{ width: '100%' }}
          />
        </FormComponent.Form>

        <ul css={{ overflowY: 'scroll', maxHeight: '290px' }}>
          {data?.data.map((c: IContent) => (
            <li
              key={c.id}
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px',
                borderBottom: '1px solid #ddd',
              }}
            >
              <p>
                <b>[{c.id}]</b> {c.title}
              </p>

              {isSelected(c) ? (
                <UiComponent.Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleRemove(c)}
                >
                  제거
                </UiComponent.Button>
              ) : (
                <UiComponent.Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSelect(c)}
                >
                  선택
                </UiComponent.Button>
              )}
            </li>
          ))}
        </ul>
        <UiComponent.Pagination
          {...pageState}
          totalPage={data?.totalPage || 0}
        />
      </div>
      <div css={selectedWrap}>
        <h3>선택된 콘텐츠</h3>
        <DndProvider backend={HTML5Backend}>
          <div
            css={{
              borderRadius: '4px',
              padding: '8px',
              overflowY: 'scroll',
              height: '290px',
            }}
          >
            {selectedContents.length === 0 ? (
              <p css={{ textAlign: 'center', color: '#999', margin: '20px 0' }}>
                선택된 콘텐츠가 없습니다.
              </p>
            ) : (
              selectedContents.map((c: IContent, index: number) => (
                <DraggableContentItem key={c.id} content={c} index={index} />
              ))
            )}
          </div>
        </DndProvider>
        <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={() => onSubmit(selectedContents)}
          >
            저장하기
          </UiComponent.Button>
        </div>
      </div>
    </div>
  );
}

export default SelectContentsModal;
